import { UnauthorizedError, ForbiddenError, SessionExpiredError } from './errors'
import { logger } from './logger'
import { supabase, isSupabaseEnabled } from '@/lib/supabase'
import { isLocalDevBypass } from './devMode'

let unauthorizedRedirectInProgress = false

/**
 * Ensure we only handle an unauthorized response once per page load.
 * Signs out of Supabase (when enabled) and forces a redirect to AIEW login.
 */
export async function handleUnauthorizedRedirect() {
  // In local dev mode, don't redirect - just log a warning
  if (isLocalDevBypass()) {
    logger.warn('🔧 [DEV MODE] Suppressing unauthorized redirect - dev mode is active');
    return;
  }

  if (unauthorizedRedirectInProgress) {
    return
  }

  unauthorizedRedirectInProgress = true

  try {
    if (isSupabaseEnabled && supabase) {
      await supabase.auth.signOut()
    }
  } catch (error) {
    logger.error('Failed to sign out during unauthorized redirect', error)
  } finally {
    try {
      if (typeof window !== 'undefined') {
        // Redirect to AIEW login instead of defunct /login page
        const AIEW_BASE_URL = import.meta.env.VITE_AIEW_URL || "https://ai-edge.io";
        const redirectUrl = window.location.origin;
        window.location.assign(`${AIEW_BASE_URL}/login?redirect=${encodeURIComponent(redirectUrl)}`);
      }
    } catch (navigateError) {
      logger.error('Failed to redirect after unauthorized response', navigateError)
    }
  }
}

export interface AuthErrorHandler {
  onUnauthorized?: () => void | Promise<void>
  onForbidden?: () => void | Promise<void>
  onSessionExpired?: () => void | Promise<void>
}

class AuthErrorManager {
  private handlers: AuthErrorHandler = {}

  setHandlers(handlers: AuthErrorHandler) {
    this.handlers = { ...this.handlers, ...handlers }
  }

  async handleAuthError(error: unknown): Promise<boolean> {
    if (error instanceof UnauthorizedError && this.handlers.onUnauthorized) {
      logger.info('Handling unauthorized error')
      await this.handlers.onUnauthorized()
      return true
    }

    if (error instanceof ForbiddenError && this.handlers.onForbidden) {
      logger.info('Handling forbidden error')
      await this.handlers.onForbidden()
      return true
    }

    if (error instanceof SessionExpiredError && this.handlers.onSessionExpired) {
      logger.info('Handling session expired error')
      await this.handlers.onSessionExpired()
      return true
    }

    return false
  }
}

export const authErrorManager = new AuthErrorManager()

// Example usage:
// authErrorManager.setHandlers({
//   onUnauthorized: () => {
//     window.location.href = '/login'
//   },
//   onSessionExpired: async () => {
//     await refreshToken()
//   },
//   onForbidden: () => {
//     toast.error('You do not have permission to access this resource')
//   }
// })
